import { useCallback, useEffect, useState } from 'preact/hooks';
import type { Asset } from '@schibsted-svp/svp-api-types';

import type { AdStartedParam } from '../../../types/global';
import { getAssetAspectRatio } from '../../../utils/player/asset';
import { observeViewportResize } from '../../../utils/observeViewportResize';

import { usePlayerEvent } from './usePlayerEvent';
import { useJWPlayerEvent } from './useJWPlayerEvent';

export const useFloatingAspectRatio = (asset: Asset | undefined) => {
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null);
  const [pauseAdAspectRatio, setPauseAdAspectRatio] = useState<number | null>(
    null
  );

  const handleMeta = useCallback(
    ({
      metadataType,
      width,
      height,
    }: {
      metadataType: string;
      width?: number;
      height?: number;
    }) => {
      if (metadataType !== 'media' || !width || !height) {
        return;
      }
      setVideoAspectRatio(width / height);
    },
    []
  );

  const handleVisualQuality = useCallback(
    ({ level }: jwplayer.VisualQualityParam) => {
      const { width, height } = level ?? {};
      if (width && height) {
        setVideoAspectRatio((current: number | null) =>
          current === null ? width / height : current
        );
      }
    },
    []
  );

  const handleAdStarted = useCallback(({ aspectRatio }: AdStartedParam) => {
    if (aspectRatio) {
      setVideoAspectRatio(aspectRatio);
    }
  }, []);

  const handlePauseAdVisible = useCallback(
    ({ width, height }: { width?: number; height?: number }) => {
      if (width && height) {
        setPauseAdAspectRatio(width / height);
      }
    },
    []
  );

  const handlePauseAdClosed = useCallback(() => {
    setPauseAdAspectRatio(null);
  }, []);

  const handleAdSlotComplete = useCallback(() => {
    setVideoAspectRatio(null);
  }, []);

  const handlePlaylistItem = useCallback(() => {
    setVideoAspectRatio(null);
    setPauseAdAspectRatio(null);
  }, []);

  useJWPlayerEvent('meta', handleMeta);
  useJWPlayerEvent('visualQuality', handleVisualQuality);
  usePlayerEvent('adStarted', handleAdStarted);
  usePlayerEvent('adSlotComplete', handleAdSlotComplete);
  usePlayerEvent('playlistItem', handlePlaylistItem);
  usePlayerEvent('PausePlugin:adVisible', handlePauseAdVisible);
  usePlayerEvent('PausePlugin:pauseAdClosed', handlePauseAdClosed);
  usePlayerEvent('play', handlePauseAdClosed);

  return pauseAdAspectRatio ?? videoAspectRatio ?? getAssetAspectRatio(asset);
};

/**
 * Tracks `window.innerWidth`, updating whenever the viewport is resized.
 */
export const useViewportWidth = () => {
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const { unsubscribe } = observeViewportResize(() => {
      setViewportWidth(window.innerWidth);
    });
    return () => unsubscribe();
  }, []);

  return viewportWidth;
};
